// Resolve API base URL at runtime from hostname (avoids build/cache issues)
function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.aeglazing.co.uk') return 'https://api-app.aeglazing.co.uk/api/v1';
    if (host === 'staging.app.aeglazing.co.uk') return 'https://api-staging.app.aeglazing.co.uk/api/v1';
  }
  return import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api/v1';
}

// Centralized configuration for ALL features
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'Glassshop',
  VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  ENVIRONMENT: import.meta.env.VITE_APP_ENVIRONMENT || 'development',
  get API_URL() {
    return getApiBaseUrl();
  },
} as const;

// API Endpoints - Centralized for ALL features
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login/',
    LOGOUT: '/auth/logout/',
    REFRESH: '/auth/token/refresh/',
    REGISTER: '/auth/register/',
  },
  // Users & Roles
  USERS: '/user-profiles/',
  ROLES: '/roles/',
  PERMISSIONS: '/permissions/',
  USER_ACTIVITIES: '/user-activities/',
  // Jobs
  JOBS: '/jobs/',
  // Production
  PRODUCTION_JOBS: '/production-jobs/',
  // Orders
  ORDERS: '/orders/',
  ORDER_IMAGES: '/order-images/',
  // Glass Values
  GLASS_VALUES: '/glass-values/',
  // Email System
  EMAIL_ACCOUNTS: '/emails/accounts/',
  EMAIL_TEMPLATES: '/emails/templates/',
  EMAIL_LOGS: '/emails/logs/',
  EMAIL_SEND: '/emails/actions/send/',
  EMAIL_STATUS: '/emails/actions/status/',
  EMAIL_TEST: '/emails/actions/send_test/',
  // Health
  HEALTH: '/health/',
} as const;

// Build full API URLs - Used by ALL features
export const buildApiUrl = (endpoint: string): string => {
  return `${APP_CONFIG.API_URL}${endpoint.startsWith('/') ? endpoint.slice(1) : endpoint}`;
};

// Specific API URLs - Ready for ALL features
export const API_URLS = {
  // Authentication
  LOGIN: buildApiUrl(API_ENDPOINTS.AUTH.LOGIN),
  LOGOUT: buildApiUrl(API_ENDPOINTS.AUTH.LOGOUT),
  REFRESH: buildApiUrl(API_ENDPOINTS.AUTH.REFRESH),
  REGISTER: buildApiUrl(API_ENDPOINTS.AUTH.REGISTER),
  
  // Users & Roles
  USERS: buildApiUrl(API_ENDPOINTS.USERS),
  ROLES: buildApiUrl(API_ENDPOINTS.ROLES),
  PERMISSIONS: buildApiUrl(API_ENDPOINTS.PERMISSIONS),
  USER_ACTIVITIES: buildApiUrl(API_ENDPOINTS.USER_ACTIVITIES),
  
  // Jobs
  JOBS: buildApiUrl(API_ENDPOINTS.JOBS),
  
  // Production
  PRODUCTION_JOBS: buildApiUrl(API_ENDPOINTS.PRODUCTION_JOBS),
  
  // Orders
  ORDERS: buildApiUrl(API_ENDPOINTS.ORDERS),
  ORDER_IMAGES: buildApiUrl(API_ENDPOINTS.ORDER_IMAGES),
  
  // Glass Values
  GLASS_VALUES: buildApiUrl(API_ENDPOINTS.GLASS_VALUES),
  
  // Email System
  EMAIL_ACCOUNTS: buildApiUrl(API_ENDPOINTS.EMAIL_ACCOUNTS),
  EMAIL_TEMPLATES: buildApiUrl(API_ENDPOINTS.EMAIL_TEMPLATES),
  EMAIL_LOGS: buildApiUrl(API_ENDPOINTS.EMAIL_LOGS),
  EMAIL_SEND: buildApiUrl(API_ENDPOINTS.EMAIL_SEND),
  EMAIL_STATUS: buildApiUrl(API_ENDPOINTS.EMAIL_STATUS),
  EMAIL_TEST: buildApiUrl(API_ENDPOINTS.EMAIL_TEST),
  
  // Health
  HEALTH: buildApiUrl(API_ENDPOINTS.HEALTH),
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

export const LOCAL_STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_PREFERENCES: 'user_preferences',
} as const;
